import React from 'react'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { cm } from '~/utils/utils'
import { TasksIcon } from './icons'
import type { UsageOverviewData } from './types'

interface UsageOverviewCardProps {
  data: UsageOverviewData
  className?: string
}

interface CategoryStatItemProps {
  label: string
  count: number
  percentage: number
  tagColor: 'purple' | 'blue' | 'cyan'
}

const TAG_COLORS = {
  purple: { bg: 'bg-[rgba(133,113,244,0.1)]', text: 'text-[#8571F4]' },
  blue: { bg: 'bg-[#F9F0FF]', text: 'text-[#65558F]' },
  cyan: { bg: 'bg-[rgba(69,208,238,0.1)]', text: 'text-[#44B2CB]' }
}

const CategoryStatItem: React.FC<CategoryStatItemProps> = ({
  label,
  count,
  percentage,
  tagColor
}) => (
  <div className='flex-1 bg-[rgba(255,255,255,0.3)] border border-white rounded-[16px] px-[16px] py-[12px]'>
    <p className="font-['Geist',sans-serif] text-[11px] font-normal text-[#2E1F36] select-none">
      {label}
    </p>
    <div className='flex items-center gap-[4px] mt-[4px]'>
      <p className="font-['Geist',sans-serif] text-[16px] font-bold leading-[24px] text-[#65558F] select-none">
        {count}
      </p>
      <span
        className={cm(
          'h-[16px] px-[8px] rounded-[50px] flex items-center select-none',
          TAG_COLORS[tagColor].bg
        )}
      >
        <span
          className={cm(
            "font-['Geist',sans-serif] text-[10px] font-bold select-none",
            TAG_COLORS[tagColor].text
          )}
        >
          {percentage}%
        </span>
      </span>
    </div>
  </div>
)

export const UsageOverviewCard: React.FC<UsageOverviewCardProps> = ({ data, className }) => {
  const isCompactViewport = useCompactViewport()

  return (
    <div
      className={cm(
        'bg-[rgba(255,255,255,0.5)] border border-white rounded-[24px] flex flex-col gap-[8px]',
        isCompactViewport ? 'py-4' : 'py-[24px]',
        !className?.includes('w-') && !isCompactViewport && 'w-[460px]',
        !className?.includes('h-') && !isCompactViewport && 'h-[260px]',
        isCompactViewport && !className?.includes('w-') && 'w-full',
        className
      )}
    >
      {/* Tag */}
      <div className={isCompactViewport ? 'pl-4 pr-3' : 'pl-[24px] pr-[16px]'}>
        <span className='h-[16px] px-[4px] bg-[#F9F0FF] rounded-[50px] inline-flex items-center select-none'>
          <span className="font-['Geist',sans-serif] text-[10px] font-bold text-[#65558F] select-none">
            Usage Overview
          </span>
        </span>
      </div>

      {/* Stats Grid */}
      <div
        className={
          isCompactViewport
            ? 'grid grid-cols-2 gap-3 px-3 flex-1'
            : 'grid grid-cols-3 gap-[12px] px-[16px] flex-1'
        }
      >
        {/* Total Messages — spans 2 columns */}
        <div
          className={cm(
            'flex flex-col',
            isCompactViewport ? 'col-span-2 py-2' : 'col-span-2 py-[12px] px-[16px]'
          )}
        >
          <p className="font-['Geist',sans-serif] text-[12px] font-medium leading-[16px] text-[#2E1F36] select-none">
            Total Messages Exchanged
          </p>
          <p
            className={cm(
              "font-['Geist',sans-serif] font-bold text-[#65558F] select-none",
              isCompactViewport ? 'text-[20px]' : 'text-[24px]'
            )}
          >
            {data.total_messages_exchanged.toLocaleString()}
          </p>
        </div>

        {/* Tasks Run — 1 column, same width as category cards */}
        <div className='bg-gradient-to-br from-[rgba(255,255,255,0.32)] to-[rgba(240,210,130,0.08)] border border-white rounded-[16px] px-[16px] py-[12px]'>
          <p className="font-['Geist',sans-serif] text-[11px] font-normal text-[#2E1F36] select-none">
            Tasks Run
          </p>
          <div className='flex items-center gap-[4px] mt-[4px]'>
            <p className="font-['Geist',sans-serif] text-[16px] font-bold leading-[24px] text-[#65558F] select-none">
              {data.tasks_run}
            </p>
          </div>
        </div>

        <CategoryStatItem
          label='Coding'
          count={data.categories.coding.count}
          percentage={data.categories.coding.percentage}
          tagColor='purple'
        />
        <CategoryStatItem
          label='Testing'
          count={data.categories.testing.count}
          percentage={data.categories.testing.percentage}
          tagColor='blue'
        />
        <CategoryStatItem
          label='Documentation'
          count={data.categories.documentation.count}
          percentage={data.categories.documentation.percentage}
          tagColor='cyan'
        />
      </div>
    </div>
  )
}

export default UsageOverviewCard
